<template>
	<v-expansion-panel v-if="rule">
		<v-expansion-panel-header :hide-actions="true">
			<v-row no-gutters>
				<v-col class="ma-auto"> Validation Rule </v-col>
				<v-col>
					<v-btn
						fab
						small
						icon
						elevation="2"
						class="action-btn float-right"
						@click.stop="onRemove()"
					>
						<v-icon color="error" small>mdi-delete</v-icon>
					</v-btn>
				</v-col>
			</v-row>
		</v-expansion-panel-header>
		<v-expansion-panel-content eager>
			<v-container v-if="rule">
				<v-form ref="form" :disabled="canView && !canEdit">
					<v-row no-gutters>
						<v-col>
							<v-select
								outlined
								dense
								:rules="rules.required"
								v-model="rule.Operator"
								:allow-overflow="false"
								label="Rule"
								:items="Operators"
								item-text="Title"
								item-value="Id"
							>
								<template v-slot:item="data">
									<template v-if="isObject(data.item)">
										<v-list-item-content>{{
											data.header
										}}</v-list-item-content>
									</template>
									<template v-else>
										<v-list-item-content>
											<v-list-item-title>{{
												data.item.Title
											}}</v-list-item-title>
											<v-list-item-subtitle>{{
												data.item.Subtitle
											}}</v-list-item-subtitle>
										</v-list-item-content>
									</template>
								</template>
							</v-select>
						</v-col>
					</v-row>
					<v-row no-gutters v-if="rule.Operator && hasField">
						<v-col>
							<v-select
								:items="formFieldsForValidation"
								item-text="Label"
								item-value="Id"
								v-model="rule.FormFieldsIds"
								outlined
								multiple
								small-chips
								deletable-chips
								dense
								label="Field(s)"
								:allow-overflow="false"
								:rules="rules.requiredOne"
							></v-select>
						</v-col>
					</v-row>

					<v-row no-gutters v-if="rule.Operator && hasValue">
						<v-col>
							<v-text-field
								v-model="rule.Value"
								outlined
								dense
								label="Value"
								:rules="[rules.valueFieldValidationRules]"
							/>
						</v-col>
					</v-row>

					<v-row no-gutters v-if="rule.Operator">
						<v-col>
							<v-switch
								class="float-left"
								v-model="regularExpressionSwitch"
								hide-details="true"
								label="Regular Expression"
							></v-switch>
						</v-col>
						<v-col>
							<v-switch
								v-model="inputMaskSwitch"
								label="Input Mask"
								hide-details="true"
							></v-switch>
						</v-col>
					</v-row>
					<v-row>
						<v-col>
							<v-text-field
								v-if="regularExpressionSwitch"
								v-model="rule.RegularExpression"
								outlined
								dense
								hide-details="true"
								label="RegularExpression"
							/>
						</v-col>

						<v-col>
							<v-text-field
								v-if="inputMaskSwitch"
								v-model="rule.InputMask"
								outlined
								dense
								label="InputMask"
							/>
						</v-col>
					</v-row>
				</v-form>
			</v-container>
		</v-expansion-panel-content>
	</v-expansion-panel>
</template>
<script>
import { formFieldRule } from '../../../../enums/formFieldRuleEnum';
import { formFieldRuleOperators } from '../../../../enums/dropdownHelpers/formFieldRuleOperators';
import { mapGetters } from 'vuex';
import { FormFieldsGetters } from '../../../../store/getters';
import formFieldRuleValidationMixin from '../../../../mixins/formFieldRuleValidationMixin';
import { Permissions } from '../../../../enums/permissionsEnum';
import permissionsMixin from '../../../../mixins/permissionsMixin';
import projectSettingsMixin from '../../../../mixins/projectSettingsMixin';
import moment from 'moment';
export default {
	name: 'FormFieldValidationRule',
	props: { rule: Object, formFieldIds: Array },
	mixins: [
		formFieldRuleValidationMixin,
		permissionsMixin,
		projectSettingsMixin,
	],
	computed: {
		...mapGetters({
			formFields: [FormFieldsGetters.FORM_FIELDS],
		}),
		formFieldsForValidation() {
			let result = this.formFields.filter(
				(f) =>
					f.GridViewFieldIds.filter(
						(f, index) => f == this.formFieldIds[index]
					).length == 0 && f.HiddenBehaviour == 0
			);
			return result;
		},
		canView() {
			return this.hasPermissions([
				Permissions.Tools.FormBuilder.Fields.View,
			]);
		},
		canEdit() {
			return this.hasPermissions([
				Permissions.Tools.FormBuilder.Fields.Edit,
			]);
		},
		hasValue() {
			if (!this.rule.Operator) return false;
			switch (this.rule.Operator) {
				case formFieldRule.Accepted:
				case formFieldRule.Alpha:
				case formFieldRule.AlphaDash:
				case formFieldRule.AlphaNum:
				case formFieldRule.ConfirmedFAndSameF:
				case formFieldRule.DifferentF:
				case formFieldRule.Date:
				case formFieldRule.IntegerAndNumeric:
				case formFieldRule.Ip:
				case formFieldRule.RequiredWith:
				case formFieldRule.RequiredWithout:
				case formFieldRule.RequiredWithoutAll:
				case formFieldRule.Url:
				case formFieldRule.MaxOne:
					return false;
				default:
					return true;
			}
		},
		hasField() {
			if (!this.rule.Operator) return false;
			switch (this.rule.Operator) {
				case formFieldRule.Accepted:
				case formFieldRule.AfterD:
				case formFieldRule.Alpha:
				case formFieldRule.AlphaDash:
				case formFieldRule.AlphaNum:
				case formFieldRule.BeforeD:
				case formFieldRule.BetweenXY:
				case formFieldRule.Date:
				case formFieldRule.In:
				case formFieldRule.IntegerAndNumeric:
				case formFieldRule.Ip:
				case formFieldRule.MaxX:
				case formFieldRule.MinX:
				case formFieldRule.NotIn:
				case formFieldRule.Regex:
				case formFieldRule.Size:
				case formFieldRule.MaxOne:
					return false;
				default:
					return true;
			}
		},
	},
	data() {
		return {
			rules: {
				required: [(value) => !!value || 'Field is required'],
				requiredOne: [
					(value) => {
						if (!value) return 'Field is required';
						if (value.length == 0) return 'Field is required';
						return true;
					},
				],
				valueFieldValidationRules: (value) => {
					if (!this.rule.Operator) return 'Please Select operator';

					switch (this.rule.Operator) {
						case formFieldRule.Accepted:
							return (
								this.alphaCheck(value) ||
								'The form will be valid when the field is yes, no or 1'
							);
						case formFieldRule.AfterD:
						case formFieldRule.BeforeD:
							return (
								moment(
									value,
									this.getDateTimeFormatProjectSetting(),
									true
								).isValid() ||
								'Value must be a valid date ' +
									'(' +
									this.getDateTimeFormatProjectSetting() +
									')'
							);
						case formFieldRule.Alpha:
							return (
								this.alphaCheck(value) ||
								'This field must contain only letters'
							);
						default:
							return true;
					}
				},
			},
			Operators: formFieldRuleOperators,
			regularExpressionSwitch: false,
			inputMaskSwitch: false,
		};
	},
	watch: {
		'rule.Operator'() {
			if (this.hasValue == false) this.rule.Value = null;
			if (this.hasField == false) {
				this.rule.FormFieldsIds = [];
			}
		},
	},
	methods: {
		isObject(item) {
			return typeof item !== 'object';
		},
		onRemove() {
			this.$emit('remove', this.rule);
		},
		validate() {
			let valid = true;
			valid = valid && this.$refs.form.validate();
			return valid;
		},
	},
};
</script>
